import React, { useEffect, useState } from 'react';
import { auth, db } from '../firebase'; // Make sure to adjust the import paths
import { collection, getDocs, doc, getDoc, query, orderBy, limit, startAfter, getCountFromServer, where } from 'firebase/firestore';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';

const TransactionsPage = () => {
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [downloadingCSV, setDownloadingCSV] = useState(false);
    const [error, setError] = useState(null);
    const [walletBalance, setWalletBalance] = useState(0);
    const [lastDoc, setLastDoc] = useState(null); // For pagination
    const [hasMore, setHasMore] = useState(true); // Track if more transactions exist
    const [totalTransactionCount, setTotalTransactionCount] = useState(0); // Total count from server
    const [totalDepositCount, setTotalDepositCount] = useState(0); // Total deposit count from server
    const [cachedAllTransactions, setCachedAllTransactions] = useState(null); // Cache for all transactions
    const [expandedRows, setExpandedRows] = useState(new Set()); // Track which bulk confirmation rows are expanded
    
    const TRANSACTIONS_PER_PAGE = 15; // Adjust this number based on your needs
    const user = auth.currentUser;

    useEffect(() => {
        if (user) {
            fetchInitialTransactions();
            fetchWalletBalance();
            fetchTotalTransactionCount(); // Get total count separately
            fetchTotalDepositCount(); // Get total deposit count separately
        } else {
            setError('User not logged in. Please log in.');
            setLoading(false);
        }
    }, [user]);

    // Function to fetch total transaction count (uses count aggregation - minimal cost)
    const fetchTotalTransactionCount = async () => {
        try {
            const transactionsRef = collection(db, 'users', user.uid, 'wallet', 'walletDetails', 'transactions');
            const snapshot = await getCountFromServer(transactionsRef);
            setTotalTransactionCount(snapshot.data().count);
        } catch (error) {
            console.error('Error fetching transaction count:', error.message);
            // Fallback: if count aggregation fails, we'll show loaded count
            setTotalTransactionCount(0);
        }
    };

    // Function to fetch total deposit count (uses count aggregation with where clause - minimal cost)
    const fetchTotalDepositCount = async () => {
        try {
            const transactionsRef = collection(db, 'users', user.uid, 'wallet', 'walletDetails', 'transactions');
            const depositQuery = query(transactionsRef, where('type', '==', 'deposit'));
            const snapshot = await getCountFromServer(depositQuery);
            setTotalDepositCount(snapshot.data().count);
        } catch (error) {
            console.error('Error fetching deposit count:', error.message);
            // Fallback: if count aggregation fails, we'll show loaded count
            setTotalDepositCount(0);
        }
    };

    // Function to fetch wallet balance
    const fetchWalletBalance = async () => {
        try {
            const walletRef = doc(db, 'users', user.uid, 'wallet', 'walletDetails');
            const walletSnap = await getDoc(walletRef);

            if (walletSnap.exists()) {
                setWalletBalance(walletSnap.data().balance);
            } else {
                setError('Failed to fetch wallet balance.');
            }
        } catch (error) {
            console.error('Error fetching wallet balance:', error.message);
            setError('Failed to fetch wallet balance.');
        }
    };

    // Function to fetch initial transactions (first page)
    const fetchInitialTransactions = async () => {
        setLoading(true);
        try {
            const transactionsRef = collection(db, 'users', user.uid, 'wallet', 'walletDetails', 'transactions');
            const q = query(
                transactionsRef, 
                orderBy('timestamp', 'desc'),
                limit(TRANSACTIONS_PER_PAGE)
            );
            const querySnapshot = await getDocs(q);
            const fetchedTransactions = querySnapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
            }));
            
            setTransactions(fetchedTransactions);
            
            // Set last document for pagination
            if (querySnapshot.docs.length > 0) {
                setLastDoc(querySnapshot.docs[querySnapshot.docs.length - 1]);
            }
            
            // Check if there might be more transactions
            setHasMore(querySnapshot.docs.length === TRANSACTIONS_PER_PAGE);
            
        } catch (error) {
            console.error('Error fetching transactions:', error.message);
            setError('Failed to fetch transactions. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Function to load more transactions
    const loadMoreTransactions = async () => {
        if (!lastDoc || !hasMore) return;
        
        setLoadingMore(true);
        try {
            const transactionsRef = collection(db, 'users', user.uid, 'wallet', 'walletDetails', 'transactions');
            const q = query(
                transactionsRef,
                orderBy('timestamp', 'desc'),
                startAfter(lastDoc),
                limit(TRANSACTIONS_PER_PAGE)
            );
            const querySnapshot = await getDocs(q);
            const newTransactions = querySnapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
            }));
            
            // Append new transactions to existing ones
            setTransactions(prevTransactions => [...prevTransactions, ...newTransactions]);
            
            // Update last document for next pagination
            if (querySnapshot.docs.length > 0) {
                setLastDoc(querySnapshot.docs[querySnapshot.docs.length - 1]);
            }
            
            // Check if there are more transactions to load
            setHasMore(querySnapshot.docs.length === TRANSACTIONS_PER_PAGE);
            
        } catch (error) {
            console.error('Error loading more transactions:', error.message);
            setError('Failed to load more transactions.');
        } finally {
            setLoadingMore(false);
        }
    };

    // Function to fetch ALL transactions for CSV download
    const fetchAllTransactions = async () => {
        // Check if we already have cached all transactions
        if (cachedAllTransactions) {
            return cachedAllTransactions;
        }

        try {
            const transactionsRef = collection(db, 'users', user.uid, 'wallet', 'walletDetails', 'transactions');
            const q = query(transactionsRef, orderBy('timestamp', 'desc'));
            const querySnapshot = await getDocs(q);
            const allTransactions = querySnapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
            }));
            
            // Cache the results to avoid repeated fetches
            setCachedAllTransactions(allTransactions);
            return allTransactions;
        } catch (error) {
            console.error('Error fetching all transactions:', error.message);
            throw error;
        }
    };

    // Function to get transaction type count from currently loaded transactions
    const getTransactionTypeCount = (type) => {
        return transactions.filter(transaction => transaction.type === type).length;
    };

    // Function to get transaction type count from all transactions (for analytics)
    const getAllTransactionTypeCount = (type, allTransactions) => {
        return allTransactions.filter(transaction => transaction.type === type).length;
    };

    // Function to toggle dropdown for bulk confirmation transactions
    const toggleOrderDetails = (transactionId) => {
        const newExpandedRows = new Set(expandedRows);
        if (newExpandedRows.has(transactionId)) {
            newExpandedRows.delete(transactionId);
        } else {
            newExpandedRows.add(transactionId);
        }
        setExpandedRows(newExpandedRows);
    };

    // Function to check if transaction is bulk confirmation
    const isBulkConfirmation = (transaction) => {
        return transaction.type && transaction.type.includes('bulk_confirmation');
    };

    // Function to render transaction type with dropdown for bulk confirmations
    const renderTransactionType = (transaction) => {
        if (isBulkConfirmation(transaction)) {
            return (
                <div className="bulk-confirmation-cell">
                    <div 
                        className="bulk-confirmation-header"
                        onClick={() => toggleOrderDetails(transaction.id)}
                        style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                    >
                        <span>{transaction.type}</span>
                        <span className={`dropdown-arrow ${expandedRows.has(transaction.id) ? 'expanded' : ''}`}>
                            ▼
                        </span>
                    </div>
                    {expandedRows.has(transaction.id) && transaction.orderDetails && (
                        <div className="order-details-dropdown">
                            <div className="order-details-header">Order Details:</div>
                            {transaction.orderDetails.map((order, index) => (
                                <div key={index} className="order-detail-item">
                                    <div><strong>Order ID:</strong> {order.orderId}</div>
                                    <div><strong>Store Order ID:</strong> {order.storeOrderId}</div>
                                    <div><strong>Amount:</strong> ₹{order.amount ? order.amount.toFixed(2) : 'NA'}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            );
        }
        return transaction.type || 'NA';
    };

    // Function to download the transaction data as CSV
    const downloadCSV = async () => {
        setDownloadingCSV(true);
        try {
            // Fetch all transactions for CSV download
            const allTransactions = await fetchAllTransactions();
            
            const csvRows = [
                ["Date", "Type", "Amount (₹)", "Balance Before (₹)", "Balance After (₹)", "Order Details"], // CSV headers
                ...allTransactions.map(transaction => {
                    let orderDetailsStr = '';
                    if (isBulkConfirmation(transaction) && transaction.orderDetails) {
                        orderDetailsStr = transaction.orderDetails.map(order => 
                            `OrderID: ${order.orderId} | StoreOrderID: ${order.storeOrderId} | Amount: ₹${order.amount || 'NA'}`
                        ).join(' | ');
                    }
                    
                    return [
                        transaction.timestamp ? new Date(transaction.timestamp.toDate()).toLocaleString() : 'NA',
                        transaction.type || 'NA',
                        transaction.amount !== undefined && transaction.amount !== null ? transaction.amount : 'NA',
                        transaction.balanceBefore !== undefined && transaction.balanceBefore !== null ? transaction.balanceBefore : 'NA',
                        transaction.balanceAfter !== undefined && transaction.balanceAfter !== null ? transaction.balanceAfter : 'NA',
                        orderDetailsStr
                    ];
                })
            ];

            const csvContent = csvRows.map(e => e.join(",")).join("\n");
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

            const link = document.createElement("a");
            const url = URL.createObjectURL(blob);
            link.setAttribute("href", url);
            link.setAttribute("download", `transaction_history_complete_${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
        } catch (error) {
            console.error('Error downloading CSV:', error.message);
            setError('Failed to download CSV. Please try again.');
        } finally {
            setDownloadingCSV(false);
        }
    };

    // Function to clear cache (optional - for refresh)
    const clearCache = () => {
        setCachedAllTransactions(null);
    };

    return (
        <div className="flex h-screen bg-white">
            <Sidebar />
            <div className="flex-1 flex flex-col min-w-0 overflow-hidden md:ml-64">
                <Header />
                <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto">
                    {/* Header */}
                    <div className="mb-8">
                        <h1 className="text-3xl font-bold text-gray-800 order-management-title uppercase text-left">
                            Transaction History
                        </h1>
                        {error && (
                            <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                                {error}
                            </div>
                        )}
                    </div>

                    {loading ? (
                        <div className="flex justify-center items-center py-12">
                            <div className="text-gray-600 text-lg">Loading transactions...</div>
                        </div>
                    ) : transactions.length === 0 ? (
                        <div className="flex justify-center items-center py-12">
                            <div className="text-gray-600 text-lg">No transactions found.</div>
                        </div>
                    ) : (
                        <>
                            {/* Analytics Cards */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                                <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg hover:shadow-xl transition-shadow duration-300">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <h3 className="text-sm font-semibold text-white mb-1">Wallet Balance</h3>
                                            <p className="text-2xl font-bold">₹{walletBalance.toFixed(2)}</p>
                                        </div>
                                        <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                                            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                                                <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4zM18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z"/>
                                            </svg>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-6 text-white shadow-lg hover:shadow-xl transition-shadow duration-300">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <h3 className="text-sm font-semibold text-white mb-1">Total Transactions</h3>
                                            <p className="text-2xl font-bold">{totalTransactionCount}</p>
                                        </div>
                                        <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                                            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                                            </svg>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl p-6 text-white shadow-lg hover:shadow-xl transition-shadow duration-300">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <h3 className="text-sm font-semibold text-white mb-1">Total Deposits</h3>
                                            <p className="text-2xl font-bold">{totalDepositCount}</p>
                                        </div>
                                        <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                                            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd"/>
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Action Buttons */}
                            <div className="mb-8 flex flex-wrap gap-4">
                                <button 
                                    onClick={downloadCSV} 
                                    disabled={downloadingCSV}
                                    className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-6 py-3 rounded-lg font-medium transition-all duration-200 transform hover:scale-105 disabled:transform-none shadow-md hover:shadow-lg flex items-center gap-2"
                                >
                                    {downloadingCSV ? (
                                        <>
                                            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Downloading All Transactions...
                                        </>
                                    ) : (
                                        <>
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                                            </svg>
                                            Download CSV
                                        </>
                                    )}
                                </button>
                                
                                {cachedAllTransactions && (
                                    <button 
                                        onClick={clearCache} 
                                        className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-lg font-medium transition-all duration-200 transform hover:scale-105 shadow-md hover:shadow-lg"
                                        title="Clear cached data and refresh"
                                    >
                                        Clear Cache
                                    </button>
                                )}
                            </div>
                            
                            {/* Transactions Table */}
                            <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Date</th>
                                                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Type</th>
                                                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Amount (₹)</th>
                                                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Balance Before (₹)</th>
                                                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Balance After (₹)</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200">
                                            {transactions.map((transaction) => (
                                                <tr key={transaction.id} className="hover:bg-gray-50 transition-colors duration-150">
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                        {transaction.timestamp ? new Date(transaction.timestamp.toDate()).toLocaleString() : 'NA'}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                        {renderTransactionType(transaction)}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                        {transaction.amount !== undefined && transaction.amount !== null ? transaction.amount.toFixed(2) : 'NA'}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                        {transaction.balanceBefore !== undefined && transaction.balanceBefore !== null ? transaction.balanceBefore.toFixed(2) : 'NA'}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                        {transaction.balanceAfter !== undefined && transaction.balanceAfter !== null ? transaction.balanceAfter.toFixed(2) : 'NA'}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            
                            {/* Load More Button */}
                            {hasMore && (
                                <div className="mt-8 flex justify-center">
                                    <button 
                                        onClick={loadMoreTransactions} 
                                        disabled={loadingMore}
                                        className="bg-gray-600 hover:bg-gray-700 disabled:bg-gray-400 text-white px-8 py-3 rounded-lg font-medium transition-all duration-200 transform hover:scale-105 disabled:transform-none shadow-md hover:shadow-lg"
                                    >
                                        {loadingMore ? (
                                            <div className="flex items-center gap-2">
                                                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                Loading More...
                                            </div>
                                        ) : (
                                            'Load More'
                                        )}
                                    </button>
                                </div>
                            )}
                            
                            {!hasMore && transactions.length > 0 && (
                                <div className="mt-8 text-center">
                                    <p className="text-gray-600 text-sm">All transactions loaded.</p>
                                </div>
                            )}
                        </>
                    )}
                </main>
            </div>
        </div>
    );
};

export default TransactionsPage;